<template>
  <div>
    <el-tooltip v-if="showFab" content="AI 调试面板" placement="left">
      <button class="mcp-debug-fab" type="button" @click="open">
        <el-icon><ChatDotRound /></el-icon>
      </button>
    </el-tooltip>

    <DraggablePanel
      v-model="visible"
      title="AI 调试面板（本地模型 + MCP 工具）"
      :initial-width="860"
      :initial-height="620"
      :initial-top="80"
      :initial-left="120"
      :min-width="680"
      :min-height="460"
    >
      <template #actions>
        <el-tooltip content="清空对话" placement="bottom">
          <button class="panel-icon-btn" type="button" @click="clearAll" :disabled="busy">
            <el-icon><Delete /></el-icon>
          </button>
        </el-tooltip>
      </template>

      <el-tabs v-model="activeTab" class="mcp-tabs">
        <el-tab-pane label="对话" name="chat">
          <div class="chat-area">
            <div class="chat-history">
              <div v-if="messages.length === 0" class="chat-empty">
                发送一句话试试，比如：<code>35号线的走向</code> 或 <code>碧茵坡 英文名</code>
              </div>
              <div v-for="(msg, idx) in messages" :key="idx" class="chat-msg">
                <div class="chat-meta">
                  <el-tag :type="roleTagType(msg.role)" size="small">{{ roleLabel(msg.role) }}</el-tag>
                </div>
                <div v-if="msg.role === 'assistant'" class="chat-markdown" v-html="renderMarkdown(msg.content)"></div>
                <pre v-else class="chat-content">{{ msg.content }}</pre>
              </div>
            </div>

            <div class="chat-input">
              <el-input
                v-model="input"
                type="textarea"
                :rows="3"
                resize="none"
                placeholder="输入问题…（Shift+Enter 换行，Enter 发送）"
                @keydown.enter.exact.prevent="send"
                @keydown.enter.shift.exact.stop
              />
              <div class="chat-actions">
                <el-switch v-model="includeTrace" active-text="返回 Trace" />
                <el-button type="primary" :loading="busy" @click="send" :disabled="!input.trim()">
                  发送
                </el-button>
              </div>
              <div v-if="error" class="chat-error">
                <el-alert type="error" :closable="false" show-icon :title="error" />
              </div>
            </div>
          </div>
        </el-tab-pane>

        <el-tab-pane label="Trace" name="trace">
          <div class="trace-area">
            <div class="trace-header">
              <el-button size="small" @click="loadTools" :loading="toolsLoading">刷新工具定义</el-button>
              <el-button
                size="small"
                @click="copyTrace"
                :disabled="!activeTraceSession || (activeTraceSession.trace || []).length === 0"
              >复制 Trace</el-button>
            </div>

            <div v-if="traceSessions.length === 0" class="trace-empty">
              暂无 Trace。建议在“对话”里打开“返回 Trace”，然后发送一次请求。
            </div>

            <div v-else class="trace-sessions">
              <div class="trace-session-list">
                <div class="trace-session-title">请求记录</div>
                <div class="trace-session-items">
                  <button
                    v-for="s in traceSessions"
                    :key="s.id"
                    type="button"
                    class="trace-session-item"
                    :class="{ 'is-active': s.id === activeTraceSessionId }"
                    @click="activeTraceSessionId = s.id"
                  >
                    <div class="trace-session-query">{{ s.query }}</div>
                    <div class="trace-session-meta">
                      <span>{{ new Date(s.started_at).toLocaleString() }}</span>
                      <span v-if="s.error" class="trace-session-error">失败</span>
                      <span v-else class="trace-session-ok">成功</span>
                    </div>
                  </button>
                </div>
              </div>

              <div class="trace-session-detail">
                <div v-if="!activeTraceSession" class="trace-empty">请选择一条请求记录</div>
                <template v-else>
                  <div v-if="activeTraceSession.error" class="trace-error-block">
                    <el-alert type="error" :closable="false" show-icon :title="activeTraceSession.error" />
                  </div>

                  <el-collapse>
                    <el-collapse-item
                      v-for="(item, idx) in (activeTraceSession.trace || [])"
                      :key="idx"
                      :name="String(idx)"
                    >
                      <template #title>
                        <span class="trace-title">
                          {{ idx + 1 }}. {{ item.type || 'step' }}
                        </span>
                      </template>
                      <pre class="trace-json">{{ formatJson(item) }}</pre>
                    </el-collapse-item>
                  </el-collapse>
                </template>
              </div>
            </div>

            <div v-if="tools.length" class="tools-block">
              <div class="tools-title">工具（/api/ai/tools）</div>
              <pre class="trace-json">{{ formatJson(tools) }}</pre>
            </div>
          </div>
        </el-tab-pane>
      </el-tabs>
    </DraggablePanel>
  </div>
</template>

<script setup>
import { computed, ref } from 'vue'
import { ElMessage } from 'element-plus'
import { ChatDotRound, Delete } from '@element-plus/icons-vue'
import DraggablePanel from './DraggablePanel.vue'
import { aiApi } from '../api'
import MarkdownIt from 'markdown-it'
import DOMPurify from 'dompurify'

const props = defineProps({
  showFab: {
    type: Boolean,
    default: false
  },
  modelValue: {
    type: Boolean,
    default: false
  }
})
const emit = defineEmits(['update:modelValue'])

const visible = computed({
  get: () => props.modelValue,
  set: (v) => emit('update:modelValue', v)
})

const activeTab = ref('chat')
const input = ref('')
const busy = ref(false)
const error = ref('')
const includeTrace = ref(true)

const messages = ref([])
const traceSessions = ref([])
const activeTraceSessionId = ref(null)

const tools = ref([])
const toolsLoading = ref(false)

const md = new MarkdownIt({
  html: false,
  linkify: true,
  breaks: true
})

const sanitizeHtml = (html) => {
  try {
    return DOMPurify.sanitize(html, {
      USE_PROFILES: { html: true },
      FORBID_TAGS: ['style'],
      FORBID_ATTR: ['style', 'onerror', 'onload']
    })
  } catch (e) {
    return ''
  }
}

const renderMarkdown = (text) => {
  const src = typeof text === 'string' ? text : ''
  return sanitizeHtml(md.render(src))
}

const activeTraceSession = computed(() => {
  if (!activeTraceSessionId.value) return null
  return traceSessions.value.find(s => s.id === activeTraceSessionId.value) || null
})

const open = () => {
  visible.value = true
}

const clearAll = () => {
  messages.value = []
  traceSessions.value = []
  activeTraceSessionId.value = null
  error.value = ''
  input.value = ''
}

const roleLabel = (role) => {
  if (role === 'user') return 'User'
  if (role === 'assistant') return 'Assistant'
  if (role === 'tool') return 'Tool'
  return String(role || 'Unknown')
}

const roleTagType = (role) => {
  if (role === 'user') return 'success'
  if (role === 'assistant') return 'warning'
  if (role === 'tool') return 'info'
  return ''
}

const formatJson = (value) => {
  try {
    return JSON.stringify(value, null, 2)
  } catch (e) {
    return String(value)
  }
}

const send = async () => {
  const text = input.value.trim()
  if (!text || busy.value) return
  error.value = ''

  messages.value.push({ role: 'user', content: text })
  input.value = ''
  busy.value = true

  const sessionId = `trace_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`
  const session = {
    id: sessionId,
    started_at: new Date().toISOString(),
    query: text,
    trace: [],
    assistant: null,
    error: null
  }

  try {
    const payload = {
      messages: messages.value.map(m => ({ role: m.role, content: m.content })),
      include_trace: includeTrace.value
    }
    const resp = await aiApi.chat(payload)
    if (resp?.trace && includeTrace.value) {
      session.trace = resp.trace
    }
    if (resp?.assistant?.content) {
      session.assistant = resp.assistant
      messages.value.push({ role: 'assistant', content: resp.assistant.content })
    }

    if (includeTrace.value) {
      traceSessions.value.unshift(session)
      activeTraceSessionId.value = sessionId
      activeTab.value = 'trace'
    } else {
      activeTab.value = 'chat'
    }
  } catch (e) {
    const serverTrace = e?.data?.trace || e?.response?.data?.trace
    if (serverTrace && includeTrace.value) {
      session.trace = serverTrace
    }
    session.error = e?.data?.message || e?.response?.data?.message || e?.message || '请求失败'
    error.value = session.error

    if (includeTrace.value) {
      traceSessions.value.unshift(session)
      activeTraceSessionId.value = sessionId
      activeTab.value = 'trace'
    }
  } finally {
    busy.value = false
  }
}

const loadTools = async () => {
  toolsLoading.value = true
  try {
    const resp = await aiApi.tools()
    tools.value = resp?.tools || []
    ElMessage.success('工具定义已刷新')
  } catch (e) {
    ElMessage.error(e?.message || '刷新失败')
  } finally {
    toolsLoading.value = false
  }
}

const copyTrace = async () => {
  try {
    await navigator.clipboard.writeText(formatJson(activeTraceSession.value?.trace || []))
    ElMessage.success('Trace 已复制')
  } catch (e) {
    ElMessage.error('复制失败（可能没有剪贴板权限）')
  }
}
</script>

<style scoped>
.panel-icon-btn {
  width: 36px;
  height: 36px;
  border-radius: 10px;
  border: 1px solid rgba(148, 163, 184, 0.35);
  background: rgba(255, 255, 255, 0.9);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
}

.panel-icon-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.chat-markdown :deep(p) {
  margin: 0 0 10px;
}

.chat-markdown :deep(p:last-child) {
  margin-bottom: 0;
}

.chat-markdown :deep(code) {
  padding: 0 6px;
  border-radius: 6px;
  background: rgba(15, 23, 42, 0.06);
  font-size: 12px;
}

.chat-markdown :deep(pre) {
  padding: 10px 12px;
  border-radius: 10px;
  background: rgba(15, 23, 42, 0.06);
  overflow: auto;
}

.chat-markdown :deep(pre code) {
  padding: 0;
  background: transparent;
}

.chat-markdown :deep(table) {
  width: 100%;
  border-collapse: collapse;
  margin: 8px 0 12px;
  font-size: 12px;
}

.chat-markdown :deep(th),
.chat-markdown :deep(td) {
  border: 1px solid rgba(148, 163, 184, 0.35);
  padding: 6px 8px;
  text-align: left;
  vertical-align: top;
}

.chat-markdown :deep(th) {
  background: rgba(248, 250, 252, 0.8);
}

.trace-sessions {
  display: grid;
  grid-template-columns: 280px 1fr;
  gap: 12px;
}

.trace-session-list {
  border: 1px solid rgba(148, 163, 184, 0.25);
  border-radius: 12px;
  padding: 10px;
  background: rgba(248, 250, 252, 0.7);
  height: 520px;
  overflow: auto;
}

.trace-session-title {
  font-weight: 600;
  color: #0f172a;
  margin-bottom: 10px;
}

.trace-session-items {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.trace-session-item {
  text-align: left;
  border: 1px solid rgba(148, 163, 184, 0.25);
  background: rgba(255, 255, 255, 0.9);
  border-radius: 10px;
  padding: 10px;
  cursor: pointer;
}

.trace-session-item.is-active {
  border-color: rgba(59, 130, 246, 0.6);
  box-shadow: 0 10px 18px rgba(59, 130, 246, 0.12);
}

.trace-session-query {
  font-size: 13px;
  color: #0f172a;
  font-weight: 600;
  margin-bottom: 6px;
  word-break: break-word;
}

.trace-session-meta {
  display: flex;
  justify-content: space-between;
  gap: 8px;
  font-size: 12px;
  color: #64748b;
}

.trace-session-ok {
  color: #16a34a;
  font-weight: 600;
}

.trace-session-error {
  color: #dc2626;
  font-weight: 600;
}

.trace-session-detail {
  height: 520px;
  overflow: auto;
}

.trace-error-block {
  margin-bottom: 10px;
}

.mcp-debug-fab {
  position: fixed;
  right: 18px;
  top: 50%;
  transform: translateY(-50%);
  width: 44px;
  height: 44px;
  border-radius: 999px;
  border: 1px solid rgba(148, 163, 184, 0.35);
  background: rgba(255, 255, 255, 0.95);
  box-shadow: 0 12px 28px rgba(15, 23, 42, 0.18);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 5000;
}

.mcp-debug-fab:hover {
  background: #ffffff;
  box-shadow: 0 16px 34px rgba(15, 23, 42, 0.22);
}

.mcp-tabs {
  height: 100%;
}

.chat-area {
  display: flex;
  flex-direction: column;
  height: 520px;
  gap: 12px;
}

.chat-history {
  flex: 1;
  overflow: auto;
  padding: 10px 12px;
  border: 1px solid rgba(148, 163, 184, 0.25);
  border-radius: 12px;
  background: rgba(248, 250, 252, 0.7);
}

.chat-empty {
  color: #64748b;
  font-size: 13px;
}

.chat-msg {
  margin-bottom: 12px;
}

.chat-meta {
  margin-bottom: 6px;
}

.chat-content {
  margin: 0;
  white-space: pre-wrap;
  word-break: break-word;
  font-size: 13px;
  line-height: 1.5;
  color: #0f172a;
}

.chat-input {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.chat-actions {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.chat-error {
  margin-top: 4px;
}

.trace-area {
  height: 520px;
  overflow: auto;
  padding-right: 6px;
}

.trace-header {
  display: flex;
  gap: 8px;
  margin-bottom: 12px;
}

.trace-empty {
  color: #64748b;
  font-size: 13px;
  padding: 10px 0;
}

.trace-title {
  font-weight: 600;
  color: #0f172a;
}

.trace-json {
  margin: 0;
  padding: 10px 12px;
  background: rgba(15, 23, 42, 0.06);
  border-radius: 10px;
  overflow: auto;
  white-space: pre-wrap;
  word-break: break-word;
  font-size: 12px;
  line-height: 1.55;
  color: #0f172a;
}

.tools-block {
  margin-top: 14px;
}

.tools-title {
  font-weight: 600;
  color: #0f172a;
  margin: 10px 0 8px;
}
</style>
